import ISearchParams from '@shared/models/ISearchParams';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlertsStore } from './state/AlertsContainer';
import { useOrganizationStore } from './state/OrganizationContainer';
import useCurrentOrgDetailsFetching from './useCurrentOrgDetailsFetching';
import useScrollToElement from './useScrollToElement';
import { DEBOUNCE_MS } from '@shared/constants/Debounce';

export default function useCurrentOrgInviteSearch() {
    const { t } = useTranslation();
    const { currentOrg } = useCurrentOrgDetailsFetching();
    const invitesStore = useOrganizationStore((state) => state.invites);
    const loadingData = useOrganizationStore((state) => state.loadingData);
    const orgInvitesSearchContentId = 'org-invites-search';
    const defaultTake = 51;
    const displayLimit = defaultTake - 1;
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<ISearchParams>({
        searchText: '',
        skip: 0,
        take: defaultTake,
    });
    const invites = useMemo(() => {
        if (!currentOrg || loadingData.invites == true) return null;
        return invitesStore.find((org) => org.organizationId === currentOrg.id)?.invites ?? [];
    }, [currentOrg, invitesStore, loadingData]);

    useScrollToElement(orgInvitesSearchContentId, [debouncedQuery.skip]);

    function handleSearchQueryChange(value: string) {
        setSearchQuery(value);
    }

    const showPrevious = useMemo(() => debouncedQuery.skip && debouncedQuery.skip > 0, [debouncedQuery.skip]);
    const showNext = useMemo(() => invitesStore.length >= displayLimit, [invitesStore]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newQuery = {
                searchText: searchQuery,
                skip: 0,
                take: defaultTake,
            };
            if (newQuery.searchText !== debouncedQuery.searchText) {
                setDebouncedQuery(newQuery);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const handleNext = () => {
        const organizationStore = useOrganizationStore.getState();
        const count = organizationStore.invites.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: displayLimit + (prev.skip ?? 0),
        }));
    };

    const handlePrevious = () => {
        const organizationStore = useOrganizationStore.getState();
        const count = organizationStore.invites.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: Math.max(0, (prev.skip ?? 0) - displayLimit),
        }));
    };

    useEffect(() => {
        const trySearch = async () => {
            const organizationStore = useOrganizationStore.getState();
            if (organizationStore.loadingData.invites) return;
            if (!currentOrg) return;
            try {
                organizationStore.handleLoadingDataChange('invites', true);
                await organizationStore.loadInvites(currentOrg.id, debouncedQuery);
            } catch (e) {
                useAlertsStore.getState().showError(t('common:organization.invite.error'));
            } finally {
                useOrganizationStore.getState().handleLoadingDataChange('invites', false);
            }
        };
        trySearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery]);

    return {
        results: invites,
        orgInvitesSearchContentId,
        handleSearchQueryChange,
        searchQuery,
        showPrevious,
        showNext,
        handleNext,
        handlePrevious,
        isLoading: loadingData.invites,
    };
}
